import useWhiteHeader from 'hooks/useWhiteHeader';
import useScrollPosition from 'hooks/useScrollPosition';
import useThrowableHttpGet from 'hooks/useThrowableHttpGet';
import Router, { useRouter } from 'next/router';
import { useLocaleContext } from 'providers/LocaleProvider';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { Title } from '@seaweb/coral/components/Typography';
import { Navbar } from '@seaweb/coral/components/Layout';
import { useNavInfoContext } from 'providers/NavInfoProvider';
import NavSearchField from 'components/shared/NavSearchField';
import { trackEnterContactForm } from 'utils/ga';
import { useViewContext } from 'providers/ViewContextProvider';
import MobileMenu, { MobileMenuItem } from 'components/shared/MobileMenu';
import LinkButton from './LinkButton';

const StyledBlueLogo = styled.div`
  background: url('/images/seatalk_icon.png');
  width: 128px;
  height: 42px;
  background-size: 100% 100%;
  cursor: pointer;
`;

const StyledWhiteLogoAlt = styled.div`
  background: url('/images/seatalk_icon_alt.png');
  width: 128px;
  height: 42px;
  background-size: 100% 100%;
  cursor: pointer;
`;

const StyledTitle = styled(Title)`
  font-size: 16px;
  margin: 0;
  display: flex;
  align-items: center;

  > * {
    margin-right: 8px;
  }
`;

const StyledNav = styled(Navbar)`
  justify-content: center;
  width: 100%;
  height: 64px;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1000;
  padding: 0 !important;
  transition: background-color 0.2s ease;
`;

const InnerNavSection = styled.div`
  background-color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 64px;
  max-width: 1160px;
  padding: 0 24px;
  z-index: 1000;
  transition: background-color 0.2s ease;

  @media screen and (max-width: 600px) {
    width: calc(100% - 48px);
  }
`;

const RightActions = styled.div`
  display: flex;
  height: 64px;
  align-items: center;
  transition: all 0.2s ease 0s;
  ${(props) => (props.isMobileView ? 'position: absolute; right: 24px;' : '')}
`;

const StyledLinkButton = styled(LinkButton)`
  min-width: 114px;
  height: 40px;
  border-radius: 20px;
  padding: 4px 8px;
  margin-left: 24px;
  background: transparent;
  color: #ffffff;
  font-weight: 600;
`;

const Rectangle = styled.div`
  width: 1.5rem;
  height: 0.125rem;
  border-radius: 1px;
  background-color: #202325;
  transition: transform 0.2s ease;
  position: relative;
  transform-origin: 1px;
`;

const HamburgerDiv = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  opacity: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  color: white;
  margin-left: 24px;
`;

function NavBar() {
  const {
    isMobileMenuOpen,
    isSearchFieldExpandInMobile,
    setIsMobileMenuOpen,
    setIsSearchFieldExpandInMobile,
    setScrollDownRange,
  } = useNavInfoContext();
  const hamburgerRef = useRef(null);
  const { isMobileView } = useViewContext();
  const { locale: t } = useLocaleContext();
  const { response: categoriesResponse } = useThrowableHttpGet(
    '/api/hc/categories/'
  );
  const { pathname } = useRouter();
  const isWhiteHeader = useWhiteHeader();
  const isShowSearchField =
    (pathname !== '/' &&
      pathname !== '/search' &&
      pathname.indexOf('/contact') === -1) ||
    (isWhiteHeader && pathname.indexOf('/contact') === -1);
  const isShowContactUs = pathname.indexOf('/contact') === -1;
  // Use global className for modifying nprogress styles
  // nprogress styles will change when Navbar styles change
  const navbarClass = isWhiteHeader ? 'white-header' : 'blue-header';
  const recBackgroundColor = isWhiteHeader ? '#202325' : 'white';

  const openMobileMenu = () => setIsMobileMenuOpen(true);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsSearchFieldExpandInMobile(false);
  };

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen || isSearchFieldExpandInMobile) {
      closeMobileMenu();
    } else {
      openMobileMenu();
    }
  };

  useEffect(() => {
    Router.events.on('routeChangeComplete', () => {
      setIsMobileMenuOpen(false);
      setIsSearchFieldExpandInMobile(false);
    });
  }, [setIsMobileMenuOpen, setIsSearchFieldExpandInMobile]);

  useScrollPosition(({ currPos }) => {
    setScrollDownRange(-currPos.y);
  });

  useEffect(() => {
    if (!isWhiteHeader) {
      setIsSearchFieldExpandInMobile(false);
    }
  }, [isWhiteHeader, setIsSearchFieldExpandInMobile]);

  return (
    <>
      <StyledNav className={navbarClass}>
        <InnerNavSection className={navbarClass}>
          {!isSearchFieldExpandInMobile && (
            <Link href="/" passHref>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a>
                <StyledTitle
                  style={{ color: isWhiteHeader ? '#202325' : 'white' }}
                >
                  {isWhiteHeader ? (
                    <StyledBlueLogo aria-label="SeaTalk" />
                  ) : (
                    <StyledWhiteLogoAlt aria-label="SeaTalk" />
                  )}
                  {/* use span because of: https://guoyunhe.me/en/2019/06/02/fix-react-issues-with-google-translate/ */}
                  {!isMobileView && <span>{t('hc.help_center')}</span>}
                </StyledTitle>
              </a>
            </Link>
          )}

          <RightActions isMobileView={isMobileView}>
            {isMobileView ? (
              <>
                {isShowSearchField && (
                  <NavSearchField isWhiteHeader={isWhiteHeader} />
                )}

                <HamburgerDiv onClick={toggleMobileMenu} ref={hamburgerRef}>
                  <Rectangle
                    style={{
                      backgroundColor: recBackgroundColor,
                      transform:
                        isMobileMenuOpen || isSearchFieldExpandInMobile
                          ? 'rotate(45deg)'
                          : 'rotate(0)',
                    }}
                  />
                  <Rectangle
                    style={{
                      backgroundColor: recBackgroundColor,
                      visibility:
                        isMobileMenuOpen || isSearchFieldExpandInMobile
                          ? 'hidden'
                          : '',
                    }}
                  />
                  <Rectangle
                    style={{
                      backgroundColor: recBackgroundColor,
                      transform:
                        isMobileMenuOpen || isSearchFieldExpandInMobile
                          ? 'rotate(-45deg)'
                          : 'rotate(0)',
                    }}
                  />
                </HamburgerDiv>
              </>
            ) : (
              <>
                {isShowSearchField && (
                  <NavSearchField
                    isWhiteHeader={isWhiteHeader}
                    style={{
                      opacity: 0.5,
                      borderRadius: '4px',
                    }}
                  />
                )}
                {isShowContactUs && (
                  <StyledLinkButton
                    radius={20}
                    variant={isWhiteHeader ? 'contained' : 'outlined'}
                    style={{
                      backgroundImage: isWhiteHeader
                        ? 'linear-gradient(140deg, #3ac4fb 9%, #178bec 93%)'
                        : 'none',
                      backgroundColor: isWhiteHeader ? 'none' : 'white',
                      color: isWhiteHeader ? 'white' : '#1b92f5',
                    }}
                    href="/contact"
                    onClick={trackEnterContactForm}
                  >
                    {t('hc.contact.contact_us')}
                  </StyledLinkButton>
                )}
              </>
            )}
          </RightActions>
        </InnerNavSection>

        {isMobileView && (
          <MobileMenu>
            {categoriesResponse &&
              categoriesResponse.categories &&
              categoriesResponse.categories.map((category) => (
                <Link key={category.id} href={`/categories/${category.id}`}>
                  <MobileMenuItem onClick={closeMobileMenu}>
                    {category.name}
                  </MobileMenuItem>
                </Link>
              ))}
            {isShowContactUs && (
              <Link href="/contact">
                <MobileMenuItem
                  onClick={() => {
                    closeMobileMenu();
                    trackEnterContactForm();
                  }}
                >
                  {t('hc.contact.contact_us')}
                </MobileMenuItem>
              </Link>
            )}
          </MobileMenu>
        )}
      </StyledNav>
    </>
  );
}

export default NavBar;
